import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import { 
  KanbanCard, 
  KanbanCardTag, 
  KanbanCardComment, 
  KanbanCardAttachment, 
  KanbanColumn 
} from "@/types/database";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from "@/components/ui/drawer";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent
} from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { 
  CalendarIcon, 
  Clock, 
  Tag, 
  Paperclip, 
  MessageSquare, 
  Trash2, 
  File, 
  FileImage,
  FileAudio,
  FileVideo,
  Download,
  X, 
  Loader2,
  ImageIcon,
  ChevronLeft
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "@/lib/toast";
import { UserSelectField } from "./UserSelectField";
import { CardCoverManager } from "./CardCoverManager";
import { DeliveryCountdown } from "./DeliveryCountdown";
import { BoardTagSelector } from "./BoardTagSelector";
import { useIsMobile } from "@/hooks/use-mobile";

interface CardDetailsDialogProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  card: KanbanCard;
  columnId: string;
  onCardUpdated: () => void;
}

export function CardDetailsDialog({
  isOpen,
  setIsOpen,
  card,
  columnId,
  onCardUpdated
}: CardDetailsDialogProps) {
  const isMobile = useIsMobile();
  const [title, setTitle] = useState(card.title);
  const [description, setDescription] = useState(card.description || "");
  const [dueDate, setDueDate] = useState<Date | undefined>(
    card.due_date ? new Date(card.due_date) : undefined
  );
  const [dueTime, setDueTime] = useState(
    card.due_date ? format(new Date(card.due_date), "HH:mm") : "09:00"
  );
  const [assignee, setAssignee] = useState(card.assignee || "");
  const [newComment, setNewComment] = useState("");
  const [activeTab, setActiveTab] = useState("description");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTagInput, setShowTagInput] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState("#0448FF");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [isSavingDescription, setIsSavingDescription] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [coverImageUrl, setCoverImageUrl] = useState(card.cover_image_url || "");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  useEffect(() => {
    setTitle(card.title);
    setDescription(card.description || "");
    setDueDate(card.due_date ? new Date(card.due_date) : undefined);
    setDueTime(card.due_date ? format(new Date(card.due_date), "HH:mm") : "09:00");
    setAssignee(card.assignee || "");
    setCoverImageUrl(card.cover_image_url || "");
    setIsEditingTitle(false);
    setHasUnsavedChanges(false);
  }, [card]);

  // Track changes to enable auto-save
  useEffect(() => {
    const hasChanges = 
      title !== card.title ||
      description !== (card.description || "") ||
      (dueDate?.toISOString() !== card.due_date) ||
      assignee !== (card.assignee || "") ||
      coverImageUrl !== (card.cover_image_url || "");
    
    setHasUnsavedChanges(hasChanges);
  }, [title, description, dueDate, dueTime, assignee, coverImageUrl, card]);

  // Auto-save when dialog closes
  useEffect(() => {
    return () => {
      if (hasUnsavedChanges) {
        handleAutoSave();
      }
    };
  }, [hasUnsavedChanges]);
  
  const { data: cardTags = [], refetch: refetchTags } = useQuery({
    queryKey: ["card_tags", card.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("kanban_card_tags")
        .select("*")
        .eq("card_id", card.id);
        
      if (error) throw error;
      return data as KanbanCardTag[];
    },
    enabled: isOpen
  });

  const { data: comments = [], refetch: refetchComments, isLoading: isLoadingComments } = useQuery({
    queryKey: ["card_comments", card.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("kanban_card_comments")
        .select("*")
        .eq("card_id", card.id)
        .order("created_at", { ascending: true });
        
      if (error) throw error;
      return data as KanbanCardComment[];
    },
    enabled: isOpen
  });

  const { data: attachments = [], refetch: refetchAttachments, isLoading: isLoadingAttachments } = useQuery({
    queryKey: ["card_attachments", card.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("kanban_card_attachments")
        .select("*")
        .eq("card_id", card.id)
        .order("created_at", { ascending: false });
        
      if (error) throw error;
      return data as KanbanCardAttachment[];
    },
    enabled: isOpen
  });

  const { data: columns = [] } = useQuery({
    queryKey: ["board_columns", columnId],
    queryFn: async () => {
      const { data: columnData, error: columnError } = await supabase
        .from("kanban_columns")
        .select("board_id")
        .eq("id", columnId)
        .single();
        
      if (columnError) throw columnError;
      
      const { data, error } = await supabase
        .from("kanban_columns")
        .select("*")
        .eq("board_id", columnData.board_id)
        .order("position", { ascending: true });
        
      if (error) throw error;
      return data as KanbanColumn[];
    },
    enabled: isOpen
  });

  const { data: boardData } = useQuery({
    queryKey: ["card_board", columnId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("kanban_columns")
        .select("board_id")
        .eq("id", columnId)
        .single();
        
      if (error) throw error;
      return data;
    },
    enabled: isOpen
  });

  const handleAutoSave = async () => {
    if (!hasUnsavedChanges) return;
    
    try {
      let dueDateWithTime = null;
      
      if (dueDate) {
        const [hours, minutes] = dueTime.split(':');
        const combinedDateTime = new Date(dueDate);
        combinedDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        dueDateWithTime = combinedDateTime.toISOString();
      }
      
      const { error } = await supabase
        .from("kanban_cards")
        .update({
          title: title.trim(),
          description: description.trim() || null,
          due_date: dueDateWithTime,
          assignee: assignee.trim() || null,
          cover_image_url: coverImageUrl || null
        })
        .eq("id", card.id);
        
      if (error) throw error;
      
      onCardUpdated();
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error("Erro ao salvar cartão automaticamente:", error);
    }
  };

  const handleCloseDialog = () => {
    if (hasUnsavedChanges) {
      handleAutoSave();
    }
    setIsOpen(false);
  };
  
  const handleSaveCard = async () => {
    if (!title.trim()) {
      toast.error("Título não pode ser vazio");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      let dueDateWithTime = null;
      
      if (dueDate) {
        const [hours, minutes] = dueTime.split(':');
        const combinedDateTime = new Date(dueDate);
        combinedDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        dueDateWithTime = combinedDateTime.toISOString();
      }
      
      const { error } = await supabase
        .from("kanban_cards")
        .update({
          title: title.trim(),
          description: description.trim() || null,
          due_date: dueDateWithTime,
          assignee: assignee.trim() || null,
          cover_image_url: coverImageUrl || null
        })
        .eq("id", card.id);
        
      if (error) throw error;
      
      toast.success("Cartão atualizado com sucesso!");
      onCardUpdated();
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error("Erro ao atualizar cartão:", error);
      toast.error("Erro ao atualizar cartão");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveDescription = async () => {
    if (!card) return;
    
    setIsSavingDescription(true);
    try {
      const { error } = await supabase
        .from('kanban_cards')
        .update({ 
          description: description,
          updated_at: new Date().toISOString()
        })
        .eq('id', card.id);
        
      if (error) throw error;
      
      setIsEditingDescription(false);
      onCardUpdated();
      toast.success("Descrição atualizada com sucesso!");
    } catch (error) {
      console.error("Erro ao salvar descrição:", error);
      toast.error("Erro ao salvar descrição");
    } finally {
      setIsSavingDescription(false);
    }
  };

  const handleSaveTitle = async () => {
    if (!title.trim()) {
      toast.error("Título não pode ser vazio");
      return;
    }
    
    try {
      const { error } = await supabase
        .from('kanban_cards')
        .update({ 
          title: title.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('id', card.id);
        
      if (error) throw error;
      
      setIsEditingTitle(false);
      onCardUpdated();
      toast.success("Título atualizado com sucesso!");
    } catch (error) {
      console.error("Erro ao salvar título:", error);
      toast.error("Erro ao salvar título");
    }
  };

  const handleCoverChange = (coverUrl: string | null) => {
    setCoverImageUrl(coverUrl || "");
    onCardUpdated();
  };
  
  const handleMoveCard = async (targetColumnId: string) => {
    if (targetColumnId === columnId) return;
    
    setIsSubmitting(true);
    
    try {
      const { data: targetCards, error: fetchError } = await supabase
        .from("kanban_cards")
        .select("id")
        .eq("column_id", targetColumnId)
        .eq("is_archived", false);
        
      if (fetchError) throw fetchError;
      
      const { error } = await supabase
        .from("kanban_cards")
        .update({ 
          column_id: targetColumnId,
          position: (targetCards?.length || 0) + 1
        })
        .eq("id", card.id);
        
      if (error) throw error;
      
      toast.success("Cartão movido com sucesso!");
      setIsOpen(false);
      onCardUpdated();
    } catch (error) {
      console.error("Erro ao mover cartão:", error);
      toast.error("Erro ao mover cartão");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleArchiveCard = async () => {
    setIsSubmitting(true);
    
    try {
      const { error } = await supabase
        .from("kanban_cards")
        .update({ is_archived: true })
        .eq("id", card.id);
        
      if (error) throw error;
      
      toast.success("Cartão arquivado com sucesso!");
      setIsOpen(false);
      onCardUpdated();
    } catch (error) {
      console.error("Erro ao arquivar cartão:", error);
      toast.error("Erro ao arquivar cartão");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();

      if (!user || authError) {
        toast.error("Usuário não autenticado");
        return;
      }

      const { error: insertError } = await supabase
        .from("kanban_card_comments")
        .insert({
          card_id: card.id,
          user_id: user.id,
          content: newComment.trim(),
          author_name: user.email,
          author_avatar: null
        });

      if (insertError) {
        console.error("Erro detalhado ao inserir comentário:", insertError);
        throw insertError;
      }

      setNewComment("");
      refetchComments();
      toast.success("Comentário adicionado!");
    } catch (error: any) {
      console.error("Erro ao adicionar comentário:", error);
      toast.error("Erro ao adicionar comentário: " + (error?.message || ""));
    }
  };
  
  const handleDeleteComment = async (commentId: string) => {
    try {
      const { error } = await supabase
        .from("kanban_card_comments")
        .delete()
        .eq("id", commentId);
        
      if (error) throw error;
      
      refetchComments();
    } catch (error) {
      console.error("Erro ao excluir comentário:", error);
      toast.error("Erro ao excluir comentário");
    }
  };
  
  const handleAddTag = async () => {
    if (!newTagName.trim()) {
      toast.error("Nome da tag não pode ser vazio");
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from("kanban_card_tags")
        .insert({
          card_id: card.id,
          name: newTagName.trim(),
          color: newTagColor
        })
        .select();
        
      if (error) throw error;
      
      setNewTagName("");
      setShowTagInput(false);
      
      refetchTags();
      onCardUpdated();
      toast.success("Tag adicionada com sucesso!");
    } catch (error) {
      console.error("Erro ao adicionar tag:", error);
      toast.error("Erro ao adicionar tag");
    }
  };
  
  const handleDeleteTag = async (tagId: string) => {
    try {
      const { error } = await supabase
        .from("kanban_card_tags")
        .delete()
        .eq("id", tagId);
        
      if (error) throw error;
      
      refetchTags();
      onCardUpdated();
      toast.success("Tag removida com sucesso!");
    } catch (error) {
      console.error("Erro ao remover tag:", error);
      toast.error("Erro ao remover tag");
    }
  };

  const handleTagAdded = (newTag: KanbanCardTag) => {
    refetchTags();
    onCardUpdated();
    toast.success("Tag adicionada com sucesso!");
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    const file = files[0];
    setIsSubmitting(true);
    setUploadProgress(10);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error("Usuário não autenticado");
        return;
      }
      
      setUploadProgress(20);
      
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `card_${card.id}/${fileName}`;
      
      setUploadProgress(40);
      
      const { error: uploadError } = await supabase.storage
        .from('attachments')
        .upload(filePath, file);
        
      if (uploadError) {
        console.error("Erro detalhado ao anexar arquivo:", uploadError);
        throw new Error(`Erro ao fazer upload: ${uploadError.message}`);
      }
      
      setUploadProgress(70);
      
      const { data: { publicUrl } } = supabase.storage
        .from('attachments')
        .getPublicUrl(filePath);
      
      setUploadProgress(80);
      
      const { error } = await supabase
        .from("kanban_card_attachments")
        .insert({
          card_id: card.id,
          name: file.name,
          url: publicUrl,
          file_type: file.type,
          size: file.size
        });
        
      if (error) throw error;
      
      setUploadProgress(100);
      refetchAttachments();
      toast.success("Arquivo anexado com sucesso!");
    } catch (error: any) {
      console.error("Erro ao anexar arquivo:", error);
      toast.error(`Erro ao anexar arquivo: ${error.message}`);
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
      event.target.value = '';
    }
  };
  
  const handleDeleteAttachment = async (attachmentId: string, url: string) => {
    try {
      const urlParts = url.split('/');
      const fileName = urlParts.pop() || "";
      const cardFolderId = urlParts.pop() || "";
      
      const filePath = `${cardFolderId}/${fileName}`;
      
      console.log("Attempting to delete file at path:", filePath);
      
      const { error: storageError } = await supabase.storage
        .from('attachments')
        .remove([filePath]);
        
      if (storageError) {
        console.error("Erro ao excluir arquivo do storage:", storageError);
        console.log("Continuando com a exclusão do registro de anexo...");
      }
      
      const { error } = await supabase
        .from("kanban_card_attachments")
        .delete()
        .eq("id", attachmentId);
        
      if (error) throw error;
      
      refetchAttachments();
      toast.success("Anexo removido com sucesso!");
    } catch (error: any) {
      console.error("Erro ao remover anexo:", error);
      toast.error(`Erro ao remover anexo: ${error.message}`);
    }
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) return <FileImage className="h-8 w-8 text-blue-500" />;
    if (fileType.startsWith('audio/')) return <FileAudio className="h-8 w-8 text-purple-500" />;
    if (fileType.startsWith('video/')) return <FileVideo className="h-8 w-8 text-red-500" />;
    return <File className="h-8 w-8 text-blue-500" />;
  };
  
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' bytes';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };
  
  const renderFilePreview = (attachment: KanbanCardAttachment) => {
    if (attachment.file_type.startsWith('image/')) {
      return (
        <div className="w-full h-32 bg-gray-100 rounded-md overflow-hidden mb-2">
          <img 
            src={attachment.url} 
            alt={attachment.name}
            className="w-full h-full object-cover" 
          />
        </div>
      );
    }
    return null;
  };
  
  const Content = () => (
    <div className="flex flex-col h-full">
      <div className={cn(
        "flex items-center justify-between mb-4",
        isMobile && "px-4"
      )}>
        {isMobile && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCloseDialog}
            className="mr-2"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        )}
        <div className="flex-1">
          {isEditingTitle ? (
            <div className="flex gap-2 items-center">
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="text-xl font-bold"
                placeholder="Título do cartão"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveTitle();
                  } else if (e.key === 'Escape') {
                    setIsEditingTitle(false);
                    setTitle(card.title);
                  }
                }}
                onBlur={handleSaveTitle}
                autoFocus
              />
            </div>
          ) : (
            <h2 
              onClick={() => setIsEditingTitle(true)}
              className={cn(
                "cursor-pointer hover:bg-accent/20 p-2 rounded-md transition-colors text-lg font-semibold",
                isMobile && "text-base"
              )}
            >
              {title}
            </h2>
          )}
        </div>
      </div>
      
      <div className={cn(
        "flex-1 overflow-hidden",
        isMobile ? "flex flex-col" : "grid grid-cols-1 md:grid-cols-3 gap-6"
      )}>
        <div className={cn(
          "overflow-hidden flex flex-col",
          isMobile ? "flex-1" : "md:col-span-2"
        )}>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
            <TabsList className={cn(
              "grid w-full grid-cols-3",
              isMobile && "text-xs"
            )}>
              <TabsTrigger value="description" className={cn(isMobile && "text-xs px-2")}>
                {isMobile ? "Desc." : "Descrição"}
              </TabsTrigger>
              <TabsTrigger value="attachments" className={cn(isMobile && "text-xs px-2")}>
                Anexos {attachments.length > 0 && `(${attachments.length})`}
              </TabsTrigger>
              <TabsTrigger value="comments" className={cn(isMobile && "text-xs px-2")}>
                {isMobile ? "Coment." : "Comentários"} {comments.length > 0 && `(${comments.length})`}
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="description" className="flex-1 overflow-auto mt-4">
              <div className="relative">
                {isEditingDescription ? (
                  <div className="space-y-2">
                    <Textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Adicione uma descrição detalhada..."
                      className={cn(
                        "text-sm",
                        isMobile ? "min-h-[120px]" : "min-h-[150px]"
                      )}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.metaKey) {
                          handleSaveDescription();
                        }
                      }}
                    />
                    <div className="flex gap-2">
                      <Button
                        onClick={handleSaveDescription}
                        disabled={isSavingDescription}
                        size={isMobile ? "sm" : "default"}
                      >
                        {isSavingDescription && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        Salvar
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setIsEditingDescription(false);
                          setDescription(card?.description || "");
                        }}
                        disabled={isSavingDescription}
                        size={isMobile ? "sm" : "default"}
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div 
                    onClick={() => setIsEditingDescription(true)}
                    className={cn(
                      "p-3 rounded-md border border-input bg-background hover:bg-accent/50 cursor-pointer transition-colors",
                      isMobile ? "min-h-[120px]" : "min-h-[150px]"
                    )}
                  >
                    {description ? (
                      <p className="text-sm whitespace-pre-wrap">{description}</p>
                    ) : (
                      <p className="text-sm text-muted-foreground italic">
                        Adicione uma descrição detalhada...
                      </p>
                    )}
                  </div>
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="attachments" className="flex-1 overflow-auto mt-4">
              <div className="space-y-4">
                <div className="bg-muted/50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <ImageIcon className="h-4 w-4" />
                    Capa do Cartão
                  </h4>
                  <CardCoverManager
                    cardId={card.id}
                    currentCoverUrl={coverImageUrl}
                    onCoverChange={handleCoverChange}
                  />
                </div>
                
                <Separator />
                
                <div>
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <Paperclip className="h-4 w-4" />
                    Anexos
                  </h4>
                  
                  <div className="flex items-center gap-2 mb-4">
                    <Label htmlFor="file-upload" className="cursor-pointer">
                      <div className="flex items-center gap-2 py-2 px-4 rounded-md bg-primary/10 hover:bg-primary/20 transition-colors">
                        <Paperclip className="h-4 w-4" />
                        <span>Adicionar Anexo</span>
                      </div>
                    </Label>
                    <Input
                      id="file-upload"
                      type="file"
                      className="hidden"
                      onChange={handleFileUpload}
                      disabled={isSubmitting}
                    />
                    
                    {uploadProgress > 0 && uploadProgress < 100 && (
                      <div className="flex-1">
                        <div className="h-2 bg-gray-200 rounded-full">
                          <div 
                            className="h-2 bg-blue-600 rounded-full" 
                            style={{ width: `${uploadProgress}%` }}
                          ></div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {isLoadingAttachments ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : attachments.length > 0 ? (
                    <div className="space-y-4">
                      {attachments.map((attachment) => (
                        <div key={attachment.id} className="bg-muted/50 p-3 rounded-md">
                          {renderFilePreview(attachment)}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 flex-1">
                              {getFileIcon(attachment.file_type)}
                              <div className="flex-1 min-w-0">
                                <a 
                                  href={attachment.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="font-medium hover:underline text-sm break-all"
                                >
                                  {attachment.name}
                                </a>
                                <div className="text-xs text-muted-foreground">
                                  {formatFileSize(attachment.size)} • {format(new Date(attachment.created_at), "dd MMM yyyy", { locale: ptBR })}
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2 ml-2">
                              <Button
                                variant="outline"
                                size="icon"
                                asChild
                              >
                                <a href={attachment.url} download target="_blank" rel="noopener noreferrer">
                                  <Download className="h-4 w-4 text-green-500" />
                                </a>
                              </Button>
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={() => handleDeleteAttachment(attachment.id, attachment.url)}
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Paperclip className="h-8 w-8 mx-auto mb-2" />
                      <p>Nenhum anexo adicionado</p>
                      <p className="text-sm">Anexe arquivos para compartilhar com o time</p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="comments" className="flex-1 overflow-hidden flex flex-col mt-4">
              <div className="space-y-4 flex-1 overflow-auto">
                {isLoadingComments ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : comments.length > 0 ? (
                  <div className="space-y-4">
                    {comments.map((comment) => (
                      <div key={comment.id} className="flex gap-3">
                        <Avatar className={cn(
                          isMobile ? "h-6 w-6" : "h-8 w-8"
                        )}>
                          {comment.author_avatar ? (
                            <AvatarImage src={comment.author_avatar} alt={comment.author_name || ""} />
                          ) : (
                            <AvatarFallback>
                              {(comment.author_name || "?").substring(0, 2).toUpperCase()}
                            </AvatarFallback>
                          )}
                        </Avatar>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <div>
                              <span className={cn(
                                "font-medium",
                                isMobile && "text-sm"
                              )}>
                                {comment.author_name || "Usuário"}
                              </span>
                              <span className={cn(
                                "text-muted-foreground ml-2",
                                isMobile ? "text-xs" : "text-sm"
                              )}>
                                {format(new Date(comment.created_at), "dd MMM yyyy 'às' HH:mm", { locale: ptBR })}
                              </span>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className={cn(
                                isMobile ? "h-6 w-6" : "h-8 w-8"
                              )}
                              onClick={() => handleDeleteComment(comment.id)}
                            >
                              <Trash2 className="h-3 w-3 text-red-500" />
                            </Button>
                          </div>
                          <p className={cn(
                            "mt-1",
                            isMobile && "text-sm"
                          )}>
                            {comment.content}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageSquare className="h-8 w-8 mx-auto mb-2" />
                    <p>Nenhum comentário</p>
                    <p className="text-sm">Adicione comentários para discutir com o time</p>
                  </div>
                )}
              </div>
              
              <div className="mt-4 border-t pt-4">
                <div className="flex gap-3">
                  <Textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Adicionar comentário..."
                    className={cn(
                      "flex-1",
                      isMobile ? "h-16 text-sm" : "h-20"
                    )}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.ctrlKey) {
                        e.preventDefault();
                        handleAddComment();
                      }
                    }}
                  />
                </div>
                <div className="flex justify-end mt-2">
                  <Button
                    onClick={handleAddComment}
                    disabled={!newComment.trim()}
                    size={isMobile ? "sm" : "default"}
                  >
                    Comentar
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
        
        <div className={cn(
          "space-y-4",
          isMobile ? "px-4 pb-4" : ""
        )}>
          <div>
            <h3 className="text-sm font-medium mb-2">Tags</h3>
            {boardData && (
              <BoardTagSelector
                boardId={boardData.board_id}
                cardId={card.id}
                selectedTags={cardTags}
                onTagAdded={handleTagAdded}
                onTagRemoved={handleDeleteTag}
              />
            )}
          </div>
          
          <div>
            <h3 className="text-sm font-medium mb-2">Data e hora de entrega</h3>
            <div className="space-y-3">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !dueDate && "text-muted-foreground"
                    )}
                    size={isMobile ? "sm" : "default"}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dueDate ? (
                      format(dueDate, "dd 'de' MMMM, yyyy", { locale: ptBR })
                    ) : (
                      <span>Escolha uma data</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={dueDate}
                    onSelect={setDueDate}
                    initialFocus
                    className="p-3"
                  />
                  {dueDate && (
                    <div className="p-3 border-t">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDueDate(undefined)}
                        className="w-full"
                      >
                        Remover data
                      </Button>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
              
              {dueDate && (
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <Input
                    type="time"
                    value={dueTime}
                    onChange={(e) => setDueTime(e.target.value)}
                    className="flex-1"
                  />
                </div>
              )}
              
              {dueDate && (
                <div className="mt-3">
                  <DeliveryCountdown 
                    dueDate={dueDate && dueTime ? 
                      (() => {
                        const [hours, minutes] = dueTime.split(':');
                        const combinedDateTime = new Date(dueDate);
                        combinedDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
                        return combinedDateTime.toISOString();
                      })() : 
                      null
                    }
                    isCompleted={card.is_completed}
                  />
                </div>
              )}
            </div>
          </div>
          
          <div>
            <h3 className="text-sm font-medium mb-2">Responsável</h3>
            <UserSelectField
              value={assignee}
              onChange={setAssignee}
              placeholder="Selecionar responsável"
            />
          </div>
          
          <div>
            <h3 className="text-sm font-medium mb-2">Mover para</h3>
            <div className="space-y-2">
              <div className="grid grid-cols-1 gap-1">
                {columns.map((col) => (
                  <Button
                    key={col.id}
                    variant={col.id === columnId ? "secondary" : "outline"}
                    size="sm"
                    onClick={() => handleMoveCard(col.id)}
                    disabled={col.id === columnId}
                    className="justify-start"
                  >
                    {col.name}
                  </Button>
                ))}
              </div>
            </div>
          </div>
          
          <div>
            <h3 className="text-sm font-medium mb-2">Ações</h3>
            <div className="space-y-2">
              <Button
                variant="destructive"
                size="sm"
                onClick={handleArchiveCard}
                disabled={isSubmitting}
                className="w-full"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Arquivar Cartão
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={handleCloseDialog}>
        <DrawerContent className="max-h-[95vh] flex flex-col">
          <DrawerHeader className="text-left border-b pb-4">
            <DrawerTitle className="sr-only">Detalhes do Cartão</DrawerTitle>
          </DrawerHeader>
          <div className="flex-1 overflow-hidden">
            <Content />
          </div>
          <DrawerFooter className="border-t pt-4">
            <Button
              onClick={handleSaveCard}
              disabled={isSubmitting}
              className="w-full"
            >
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Salvar Alterações
            </Button>
            <Button
              variant="outline"
              onClick={handleCloseDialog}
              className="w-full"
            >
              Fechar
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }
  
  return (
    <Dialog open={isOpen} onOpenChange={handleCloseDialog}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="sr-only">Detalhes do Cartão</DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-hidden">
          <Content />
        </div>
        
        <DialogFooter className="border-t pt-4">
          <Button
            variant="outline"
            onClick={handleCloseDialog}
          >
            Fechar
          </Button>
          <Button
            onClick={handleSaveCard}
            disabled={isSubmitting}
          >
            {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Salvar Alterações
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
